import { Injectable, signal } from '@angular/core';

export interface PlanningRiglessDetailsDraft {
  api14: string;
  liftMethod: string;
  field: string;
  state: string;
  area: string;
  fot: string;
  plantCode: string;
  companyCode: string;
  costCenter: string;
  latitude: string;
  longitude: string;
  failureDate: Date | null;
  localReferenceNumber: string;
  hasWbs: boolean;
  wbsNumber: string;
  jobType: string;
  jobReason: string;
  avgBoed: string;
  multiPadWell: boolean;
  comments: string;
}

export interface PlanningRiglessJobPlanDraft
  extends PlanningRiglessDetailsDraft {
  projectedJobDays: string;
  isWellRigReady: '' | 'yes' | 'no';
  preScheduleStatus: string;
  preScheduleComment: string;
  plannedCost: string;
  hasWellviewJob: boolean;
  wellviewJobList: string;
  wellviewJobId: string;
  wellviewJobType: string;
  wellviewJobCreationDate: Date | null;
}

export interface ExecutionRiglessReviewUserDraft {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  fot: string;
}

export interface ExecutionRiglessReviewDraft {
  jobClosed: boolean;
  jobEndDate: Date | null;
  // CH-RL-003 (3.2.4): confirmElectricalVariance, confirmSimops, wellviewWellboreUpdated removed per FR-056.
  rigDownDateInWellview: boolean;
  rigUpDate: Date | null;
  disableAutomaticProgression: boolean;
  jobs: string;
  failure: string;
  reason: string;
  size: string;
  depth: string;
  failureType: string;
  cause: string;
  failureComment: string;
  selectedWsmUser: ExecutionRiglessReviewUserDraft | null;
  rigReportLastPulled: Date | null;
}

export interface CloseOutRiglessDraft {
  api14: string;
  fot: string;
  area: string;
  costCenter: string;
  plantCode: string;
  companyCode: string;
  failureDate: Date | null;
  failedComponentGroup: string;
  failedComponentType: string;
  failureRemarks: string;
  failureLocation: string;
  failureDepth: string;
  componentDisposition: string;
  analysisCompletionDate: Date | null;
  primaryFailureClass: string;
  analysisRemarks: string;
  primaryFailureCause: string;
  rootFailureCause: string;
  preventativeMaintenance: boolean;
  actualJobCost: string;
  personPerformingTask: string;
  failureSolution: string;
  failureObservations: string;
  jobType: string;
  jobReason: string;
  wellviewWellboreValidated: string;
  failure: string;
  responsiblePerson: string;
}

@Injectable({
  providedIn: 'root',
})
export class PlanningRiglessDraftService {
  private _detailDrafts = signal<Record<string, PlanningRiglessDetailsDraft>>(
    {},
  );
  private _jobPlanDrafts = signal<Record<string, PlanningRiglessJobPlanDraft>>(
    {},
  );
  private _executionReviewDrafts = signal<
    Record<string, ExecutionRiglessReviewDraft>
  >({});
  private _closeOutDrafts = signal<Record<string, CloseOutRiglessDraft>>({});

  getDetailsDraft(caseId: string): PlanningRiglessDetailsDraft | null {
    const draft = this._detailDrafts()[caseId];

    return draft ? this._cloneDraft(draft) : null;
  }

  setDetailsDraft(caseId: string, draft: PlanningRiglessDetailsDraft): void {
    this._detailDrafts.update((currentDrafts) => ({
      ...currentDrafts,
      [caseId]: this._cloneDraft(draft),
    }));
  }

  getJobPlanDraft(caseId: string): PlanningRiglessJobPlanDraft | null {
    const draft = this._jobPlanDrafts()[caseId];

    return draft ? this._cloneJobPlanDraft(draft) : null;
  }

  setJobPlanDraft(caseId: string, draft: PlanningRiglessJobPlanDraft): void {
    this._jobPlanDrafts.update((currentDrafts) => ({
      ...currentDrafts,
      [caseId]: this._cloneJobPlanDraft(draft),
    }));
  }

  getExecutionReviewDraft(caseId: string): ExecutionRiglessReviewDraft | null {
    const draft = this._executionReviewDrafts()[caseId];

    return draft ? this._cloneExecutionReviewDraft(draft) : null;
  }

  setExecutionReviewDraft(
    caseId: string,
    draft: ExecutionRiglessReviewDraft,
  ): void {
    this._executionReviewDrafts.update((currentDrafts) => ({
      ...currentDrafts,
      [caseId]: this._cloneExecutionReviewDraft(draft),
    }));
  }

  getCloseOutDraft(caseId: string): CloseOutRiglessDraft | null {
    const draft = this._closeOutDrafts()[caseId];

    return draft ? this._cloneCloseOutDraft(draft) : null;
  }

  setCloseOutDraft(caseId: string, draft: CloseOutRiglessDraft): void {
    this._closeOutDrafts.update((currentDrafts) => ({
      ...currentDrafts,
      [caseId]: this._cloneCloseOutDraft(draft),
    }));
  }

  clearDetailsDraft(caseId: string): void {
    this._detailDrafts.update((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      delete nextDrafts[caseId];

      return nextDrafts;
    });
  }

  private _cloneDraft(
    draft: PlanningRiglessDetailsDraft,
  ): PlanningRiglessDetailsDraft {
    return {
      ...draft,
      failureDate: draft.failureDate ? new Date(draft.failureDate) : null,
    };
  }

  private _cloneJobPlanDraft(
    draft: PlanningRiglessJobPlanDraft,
  ): PlanningRiglessJobPlanDraft {
    return {
      ...draft,
      failureDate: draft.failureDate ? new Date(draft.failureDate) : null,
      wellviewJobCreationDate: draft.wellviewJobCreationDate
        ? new Date(draft.wellviewJobCreationDate)
        : null,
    };
  }

  private _cloneExecutionReviewDraft(
    draft: ExecutionRiglessReviewDraft,
  ): ExecutionRiglessReviewDraft {
    return {
      ...draft,
      selectedWsmUser: draft.selectedWsmUser
        ? { ...draft.selectedWsmUser }
        : null,
      jobEndDate: draft.jobEndDate ? new Date(draft.jobEndDate) : null,
      rigUpDate: draft.rigUpDate ? new Date(draft.rigUpDate) : null,
      rigReportLastPulled: draft.rigReportLastPulled
        ? new Date(draft.rigReportLastPulled)
        : null,
    };
  }

  private _cloneCloseOutDraft(
    draft: CloseOutRiglessDraft,
  ): CloseOutRiglessDraft {
    return {
      ...draft,
      failureDate: draft.failureDate ? new Date(draft.failureDate) : null,
      analysisCompletionDate: draft.analysisCompletionDate
        ? new Date(draft.analysisCompletionDate)
        : null,
    };
  }
}